import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OshTreeComponent } from '@app/components/osh/osh-tree/osh-tree.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { ProjectOshAnalysisDataService } from '@app/shared/services/project-osh-analysis-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { CreateOrEditOshAnalysisDialogData, Osh } from '@app/shared/types/models/osh/osh.model';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-project-osh-view',
  imports: [OshTreeComponent, UiPageWrapperComponent, TranslatePipe, UiButtonComponent, UiSpinnerDirective],
  templateUrl: './project-osh-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './project-osh-view.component.scss'
})
export class ProjectOshViewComponent {
  private readonly dialogService = inject(DialogService);
  private readonly activeProjectService = inject(ActiveProjectService);
  private readonly projectOshAnalysisDataService = inject(ProjectOshAnalysisDataService);
  private readonly destroyRef = inject(DestroyRef);

  protected activeProjectId: Signal<string> = this.activeProjectService.activeProjectId;
  protected projectOshAnalyses: WritableSignal<Osh[]> = signal<Osh[] | undefined>(undefined);

  public constructor() {
    effect(() => {
      if (!this.activeProjectId()) return;

      const dataSignal = this.projectOshAnalysisDataService.getProjectOshAnalyses(this.activeProjectId());
      this.projectOshAnalyses.set(dataSignal());
    });
  }
  protected openCreateOshAnalysisDialog(): void {
    this.dialogService
      .openCreateOshAnalysisDialog({ id: this.activeProjectId() })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) return;

        this.projectOshAnalysisDataService
          .addProjectOshAnalysis(this.activeProjectId(), result)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.projectOshAnalysisDataService.loadProjectOshAnalyses(this.activeProjectId(), true);
          });
      });
  }

  protected editOshAnalysis(result: CreateOrEditOshAnalysisDialogData): void {
    this.projectOshAnalysisDataService
      .editProjectOshAnalysis(this.activeProjectId(), result)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.projectOshAnalysisDataService.loadProjectOshAnalyses(this.activeProjectId(), true);
      });
  }

  protected deleteOshAnalysis(analysisId: string): void {
    this.projectOshAnalysisDataService
      .deleteOshAnalysis(this.activeProjectId(), analysisId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.projectOshAnalysisDataService.loadProjectOshAnalyses(this.activeProjectId(), true);
      });
  }
}
